import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { ExtraWorkOrder } from '../../models';
import { format, isValid } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import { getExtraWorkOrderStatusConfig } from '../../config/extraWorkOrderStatusConfig';

/**
 * Card - Componente de visualización de orden de trabajo extra en mobile
 *
 * Muestra la información principal de una orden de trabajo extra en formato de tarjeta
 */
export interface CardProps {
  item: ExtraWorkOrder;
  className?: string;
  showActions?: boolean;
}

export const Card = React.memo(function Card({ item: order, className = '', showActions = true }: CardProps) {
  const { t } = useTranslation('extraWorkOrdersModule');
  const navigate = useNavigate();
  const statusConfig = getExtraWorkOrderStatusConfig(order.status);

  // Calcular progreso
  const lines = order.lines || [];
  const doneLines = lines.filter((l) => l.status === 'DONE').length;
  const totalLines = lines.length;
  const progressText = `${doneLines}/${totalLines}`;
  const formatDateSafe = (value: string | null | undefined, formatStr: string) => {
    if (!value) return '-';
    const parsedDate = new Date(value);
    return isValid(parsedDate) ? format(parsedDate, formatStr) : '-';
  };

  return (
    <div className={`py-4 border-b border-card-border hover:bg-muted/30 transition-colors ${className}`}>
      {/* Header - Referencia, estado y acciones */}
      <div className="flex items-center gap-2 mb-3 justify-between">
        <div className="flex items-center gap-2 flex-1">
          <span className="font-medium text-base text-title">{order.ref}</span>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}
          >
            {t(`status.${order.status}`, statusConfig.label)}
          </span>
        </div>
        {showActions && (
          <Button
            variant="outline"
            size="sm"
            onClick={(e: React.MouseEvent) => {
              // Si se presiona Ctrl o Cmd, abrir en nueva pestaña
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                window.open(`/extra-work-orders/${order.id}`, '_blank');
              } else {
                // Click normal, navegar en la misma pestaña
                navigate(`/extra-work-orders/${order.id}`);
              }
            }}
            data-testid={`view-order-${order.id}`}
          >
            <FontAwesomeIcon icon={faEye} className="w-3.5 h-3.5 mr-2" />
            {t('actions.view', 'Ver')}
          </Button>
        )}
      </div>

      {/* Información principal */}
      <div className="space-y-2">
        {/* Fecha límite */}
        {order.deadline_at && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-subtext">{t('table.deadline', 'Fecha límite')}:</span>
            <span className="text-sm font-medium text-text">{formatDateSafe(order.deadline_at, 'dd/MM/yyyy')}</span>
          </div>
        )}

        {/* Mercancía */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-subtext">{t('table.merchandise', 'Mercancía')}:</span>
          <span className="text-sm font-medium text-text">
            {order.merchandise?.name || order.merchandise?.ref || order.merchandise_id || '-'}
          </span>
        </div>

        {/* Ubicación origen */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-subtext">{t('table.location', 'Ubicación origen')}:</span>
          <span className="text-sm font-medium text-text">
            {order.location?.code || order.location?.name || order.location_id || '-'}
          </span>
        </div>

        {/* Progreso */}
        {totalLines > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-subtext">{t('table.progress', 'Progreso')}:</span>
            <span className="text-sm font-medium text-text">{progressText}</span>
            <span className="text-xs text-subtext">({t('detail.lines.title', 'líneas')})</span>
          </div>
        )}

        {/* Fecha de creación */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-subtext">{t('table.created_at', 'Fecha creación')}:</span>
          <span className="text-xs text-text">{formatDateSafe(order.created_at, 'dd/MM/yyyy HH:mm')}</span>
        </div>

        {/* Notas (si existen) */}
        {order.notes && (
          <div className="mt-2 pt-2 border-t border-card-border">
            <span className="text-xs text-subtext block mb-1">{t('detail.header.notes', 'Notas')}:</span>
            <span className="text-sm text-text">{order.notes}</span>
          </div>
        )}
      </div>
    </div>
  );
});

Card.displayName = 'ExtraWorkOrderCard';
